// Port of Flutter's curtainScreen.dart (DeviceID 51).
//
// The Lottie curtain animation over three round buttons: Open · Stop · Close.
// The JSON files are the ones from the Flutter app, copied to
// public/assets/animated/json/ and played with lottie-web.
//
// Commands are UPPERCASE with no prefix — OPENCURTAIN / STOPCURTAIN /
// CLOSECURTAIN. (An older React doc had these as lowercase 'cs…' variants; the
// Dart is the authority and it sends these.)
//
// Open runs the animation forward over 8 s, Close over 7 s, Stop freezes it —
// those durations are the Dart's, chosen to roughly track the real motor.

import React, { useEffect, useRef, useState } from 'react';
import { useContentHeight } from '../../components/MobileShell';
import { Box, Typography, useTheme } from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import PauseIcon from '@mui/icons-material/Pause';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useNavigate } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import useLongPress from '../../components/useLongPress';
import { useApp } from '../../../../core/state/AppContext';
import { publishCommand } from '../../../../core/utils/deviceController';
import { getSchedules } from '../../../../core/api/api';

const OPEN_JSON = '/assets/animated/json/CurtainOpen.json';
const CLOSE_JSON = '/assets/animated/json/CurtainClose.json';

export default function CurtainScreen({ device }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId } = useApp();
  const uno = device?.UniqueNumber;

  const holderRef = useRef(null);
  const animRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [scheduled, setScheduled] = useState(false);

  const geom = (() => {
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    const panelWidth = Math.min(window.innerWidth, 560);
    const animSize = Math.min(460, Math.max(160, Math.min(panelWidth * 0.8, availH * 0.55)));
    return {
      panelWidth,
      animSize,
      btnPad: Math.min(34, Math.max(20, animSize * 0.075)),
      btnIcon: Math.min(44, Math.max(28, animSize * 0.1)),
    };
  })();

  // Load (or swap) the animation whenever the open/closed state flips.
  //
  // lottie-web is ~80 kB and this is the only screen that needs it, so it is
  // imported on demand rather than bundled into the main chunk that every user
  // downloads at startup.
  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;
    let cancelled = false;

    if (animRef.current) { animRef.current.destroy(); animRef.current = null; }

    import('lottie-web').then(({ default: lottie }) => {
      if (cancelled || !holderRef.current) return;
      animRef.current = lottie.loadAnimation({
        container: holderRef.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: isOpen ? OPEN_JSON : CLOSE_JSON,
      });
    }).catch(() => { /* animation is decorative — the buttons still work */ });

    return () => {
      cancelled = true;
      if (animRef.current) { animRef.current.destroy(); animRef.current = null; }
    };
  }, [isOpen]);

  // Clock badge — the Dart checks outputs 1..4 and shows the badge if any is set.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    Promise.all([1, 2, 3, 4].map((i) => getSchedules(uno, i)
      .then((r) => (Array.isArray(r.data) ? r.data.length > 0 : false))
      .catch(() => false)))
      .then((flags) => { if (alive) setScheduled(flags.some(Boolean)); });
    return () => { alive = false; };
  }, [uno]);

  const handle = (command) => {
    publishCommand(uno, command, userId);
    const anim = animRef.current;
    if (command === 'OPENCURTAIN') {
      setIsOpen(true);
      if (anim) { anim.goToAndStop(0, true); anim.setSpeed(anim.getDuration() / 8 || 1); anim.play(); }
    } else if (command === 'CLOSECURTAIN') {
      setIsOpen(false);
      if (anim) { anim.goToAndStop(0, true); anim.setSpeed(anim.getDuration() / 7 || 1); anim.play(); }
    } else if (anim) {
      anim.pause();
    }
  };

  const bodyPress = useLongPress(
    () => navigate(`/schedule/${uno}/1`, { state: { outputName: device?.DeviceName, device } }),
    null,
  );

  const controlButton = (label, icon, onTap) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        onClick={onTap}
        sx={{
          bgcolor: c.button,
          borderRadius: '50%',
          p: `${geom.btnPad}px`,
          display: 'flex',
          cursor: 'pointer',
          boxShadow: `0 6px 12px ${c.text}26`,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: geom.btnIcon, color: c.text } })}
      </Box>
      <Box sx={{ height: 8 }} />
      <Typography sx={{ fontWeight: 600, color: c.text }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box {...bodyPress} sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
        {scheduled && (
          <ScheduleIcon sx={{
            position: 'absolute', top: 8, left: 8, fontSize: 30, color: c.button,
          }}
          />
        )}

        <Box sx={{
          maxWidth: geom.panelWidth,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
        >
          <Box ref={holderRef} sx={{ width: geom.animSize, maxWidth: '100%' }} />

          <Box sx={{ height: 30 }} />

          <Box sx={{
            display: 'flex', justifyContent: 'space-evenly', width: '100%',
          }}
          >
            {controlButton('Open', <KeyboardArrowLeftRoundedIcon />, () => handle('OPENCURTAIN'))}
            {controlButton('Stop', <PauseIcon />, () => handle('STOPCURTAIN'))}
            {controlButton('Close', <KeyboardArrowRightRoundedIcon />, () => handle('CLOSECURTAIN'))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
